import React, { useCallback, useMemo } from 'react'
import {
  FormSubentityTypeEnum,
  FormInterface,
  FormLayoutEnum,
} from 'src/shared/types/entities/form-interface'
import { EntityBorderGroup } from 'src/editor/shared/settings/wrappers/border-groups/entity-border-group'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import {
  useUpdateEntity,
  useUpdatePropNew,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import { FormLayoutSwitcher } from './form-layout-switcher'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SettingsNestedMenu } from 'src/editor/layout/sidebar/settings/settings-nested-menu'
import { FormFieldsCommonSettings } from './form-fields-common-settings'
import { FormButtonCommonSettings } from './form-button-common-settings'
import { useTranslation } from 'react-i18next'
import { Switcher } from 'src/editor/shared/ui-lib/switcher'
import { WebsiteSettingsTypographyTypeEnum } from 'src/shared/types/website-settings-types'
import { FormCheckboxesCommonSettings } from './form-checkboxes-common-settings'
import { SelectNew } from 'src/editor/shared/ui-lib/select-new/select-new'
import { AlignSelfType } from 'common/types/styleTypes'
import { getSettingsEditing } from 'client/reducers/managementReducer'
import { useManagement, usePage } from 'client/store'
import { setSettingsEditing } from 'client/store/management/managementActions'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import { getMobileOrDesktop } from 'src/shared/utils/get-mobile-or-desktop'
import { isWebsitePageType } from 'client/store/page/pageSelectors'

interface FormDesignSettingsProps {
  entity: FormInterface
}

export const FormDesignSettings = ({ entity }: FormDesignSettingsProps) => {
  const { isMobile } = useDeviceModeContext()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const isWebsitePage = usePage(isWebsitePageType)
  const editingFormCommonSettingsType =
    useManagement(getSettingsEditing).commonSettingsType

  const updateEntity = useUpdateEntity<FormInterface>()
  const updateProp = useUpdatePropNew(entity)

  const buttonPositionOptions = useMemo(
    () => [
      { value: 'flex-start' as AlignSelfType, label: t('left') },
      { value: 'center' as AlignSelfType, label: t('center') },
      { value: 'flex-end' as AlignSelfType, label: t('right') },
      { value: 'full' as const, label: t('full') },
    ],
    [t],
  )

  const resetCommonSettings = useCallback(() => {
    dispatch(
      setSettingsEditing({
        id: entity.id,
        type: entity.type,
        commonSettingsType: undefined,
      }),
    )
  }, [entity.id, entity.type, dispatch])

  return (
    <>
      {!isMobile && (
        <>
          <GroupTitle withoutHorizontalLine>
            entity_settings.form.layout
          </GroupTitle>
          <FormLayoutSwitcher
            className="mt-4"
            value={entity.layout}
            update={updateProp('layout')}
          />
        </>
      )}
      <RangeElement
        label="entity_settings.form.design.row_gap"
        value={getMobileOrDesktop(entity.rowGap, entity.mobileRowGap, isMobile)}
        onChange={updateProp(
          getMobileOrDesktop('rowGap', 'mobileRowGap', isMobile),
        )}
      />
      {!isMobile && entity.layout === FormLayoutEnum.TwoColumns && (
        <RangeElement
          label="entity_settings.form.design.column_gap"
          value={entity.columnGap}
          onChange={updateProp('columnGap')}
        />
      )}
      <ColorPickerControl
        label="settings_styles.background_color.label"
        color={entity.backgroundColor}
        update={updateProp('backgroundColor')}
        mobileColor={entity.mobileBackgroundColor}
        mobileUpdate={updateProp('mobileBackgroundColor')}
      />

      <EntityBorderGroup
        entity={entity}
        propertyKey="border"
        mobilePropertyKey="mobileBorder"
      />

      <SizeAndPositionGroup
        padding={isMobile ? entity.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />

      <GroupTitle>entity_settings.form.design.common_fields.title</GroupTitle>
      <SettingsNestedMenu
        menuTitle={t('entity_settings.form.design.common_fields.title')}
        menuButtonLabel={t('settings.sidebar.customize')}
        menuTitleClassName="mb-6"
        isOpen={
          typeof editingFormCommonSettingsType === 'undefined'
            ? undefined
            : editingFormCommonSettingsType === FormSubentityTypeEnum.Field
        }
        onClose={resetCommonSettings}
      >
        <FormFieldsCommonSettings entity={entity} />
      </SettingsNestedMenu>

      <GroupTitle>entity_settings.form.design.common_button.title</GroupTitle>
      {isWebsitePage && (
        <Switcher
          labelText="settings_styles.type.label"
          btnList={[
            {
              value: WebsiteSettingsTypographyTypeEnum.PrimaryButton,
              content:
                'website_styles_settings.buttons_and_links.primary.title',
            },
            {
              value: WebsiteSettingsTypographyTypeEnum.SecondaryButton,
              content:
                'website_styles_settings.buttons_and_links.secondary.title',
            },
          ]}
          value={entity.buttonCommonStyles.buttonType}
          update={updateProp('buttonCommonStyles', 'buttonType')}
        />
      )}
      <SelectNew
        isSearchable={false}
        labelText="entity_settings.form.design.common_button.position"
        selectedOption={
          buttonPositionOptions.find(option =>
            entity.buttonCommonStyles.width === '100%'
              ? option.value === 'full'
              : option.value === entity.buttonCommonStyles.alignSelf,
          ) ?? null
        }
        options={buttonPositionOptions}
        onChange={async option => {
          if (option) {
            if (option.value === 'full') {
              updateProp('buttonCommonStyles', 'width')('100%')
            } else {
              updateEntity({
                ...entity,
                buttonCommonStyles: {
                  ...entity.buttonCommonStyles,
                  width: 'auto',
                  alignSelf: option.value,
                },
              })
            }
          }
        }}
      />
      <GroupTitle className="mb-0" />
      <SettingsNestedMenu
        menuTitle={t('entity_settings.form.design.common_button.title')}
        menuButtonLabel={t('settings.sidebar.customize')}
        menuTitleClassName="mb-6"
        isOpen={
          typeof editingFormCommonSettingsType === 'undefined'
            ? undefined
            : editingFormCommonSettingsType ===
              FormSubentityTypeEnum.SubmitButton
        }
        onClose={resetCommonSettings}
      >
        <FormButtonCommonSettings entity={entity} />
      </SettingsNestedMenu>

      <GroupTitle>entity_settings.form.design.common_checkbox.title</GroupTitle>
      <SettingsNestedMenu
        menuTitle={t('entity_settings.form.design.common_checkbox.title')}
        menuButtonLabel={t('settings.sidebar.customize')}
        menuTitleClassName="mb-6"
        isOpen={
          typeof editingFormCommonSettingsType === 'undefined'
            ? undefined
            : editingFormCommonSettingsType === FormSubentityTypeEnum.Checkbox
        }
        onClose={resetCommonSettings}
      >
        <FormCheckboxesCommonSettings entity={entity} />
      </SettingsNestedMenu>

      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}
